import React from "react";
import {
  OnGoingClassCard,
  OnGoingClassCardContainer,
  OnGoingClassCardImageContainer,
  OngoingClassCardDetails,
  OnGoingClassCardImageWrapper,
  OngoingClassCardTopicsContainer,
  OngoingClassCardDetailsHeading,
  OngoingClassCardDetailsContent,
  OngoingClassCardDetailsTeacherContainer,
  OngoingClassCardDetailsTeacher,
  OngoingClassCardInfoItem,
  OngoingClassCardInfoItemContainer,
  OngoingClassCardProgressContainer,
  OngoingClassCardProgressBarContainer,
  OngoingClassCardProgressBarActive,
  OngoingClassCardProgressMessage,
  ClassTagContainer
} from "./ongoing-class-card.styles";

import Image from "next/legacy/image";
import { GuruJuaraBadge } from "../assets/guru-juara-badge";
import { IOngoingClassCardProps } from "./ongoing-class-card.types";
import {
  getClassProgressPercentage,
  getClassTimeStamp,
  getMediaURL,
  getMinutesElapsed,
  getTimeStampFromDateString
} from "../utils";
import { format, differenceInMinutes } from "date-fns";
import { SubjectTag } from "components/subject-tag/subject-tag";
import { JoinClass } from "../join-class/join-class";
import { ClassSessionType } from "../hooks/use-user-landing/use-user-landing.types";
import { SubjectTagData } from "components/subject-tag/subject-tag.data";
import trackEvent from "services/mixpanel/mixpanel";
import { kelasLiveEvents } from "services/mixpanel/kelas-live-events";
import { ClassTags } from "../class-tags/class-tags";
import { progressMessageThreshold } from "constants/constants";
import { StatusDot } from "../assets/status-dot/status-dot";
import { SubscriptionType } from "services/api/sessions/sessionService.types";
import { theme } from "styles/theme";
import { useBoolean } from "hooks/use-boolean/use-boolean";
import { PauseSubscriptionModal } from "../pause-subscription-modal/pause-subscription-modal";

const OngoingClassCard: React.FC<IOngoingClassCardProps> = ({
  subscriptionStatus,
  classSession,
  baseMediaURL,
  currentTimeStamp,
  primaryUser,
  handleShowMoreDetails,
  handleJoinClass,
  handleJoinQnA,
  tag,
  isHeroSection
}) => {
  const classProgressPercentage = getClassProgressPercentage(
    differenceInMinutes(
      getTimeStampFromDateString(classSession.endDate),
      getTimeStampFromDateString(currentTimeStamp)
    ),
    classSession.duration
  );
  const subjectTagData = SubjectTagData[classSession.subject.toUpperCase()];
  const minutesElapsed = getMinutesElapsed(
    currentTimeStamp,
    classSession.startDate
  );

  const { subscriptionType } = subscriptionStatus;
  const isJoinButtonDisabled =
    subscriptionType === SubscriptionType.FREE_AFTER_TRIAL ||
    subscriptionType === SubscriptionType.FREE_AFTER_PAID;

  const [isPausedSubscriptionModalOpen, setIsPausedSubscriptionModalOpen] =
    useBoolean();

  const handleClick = () => {
    trackEvent({
      eventName: kelasLiveEvents.ongoingClassDetailClicked
    });
    if (isJoinButtonDisabled) {
      handleShowMoreDetails();
    } else {
      if (subscriptionType === SubscriptionType.PAUSED) {
        setIsPausedSubscriptionModalOpen.on();
      } else {
        handleJoinClass(classSession);
      }
    }
  };

  const getBackgroundColor = () => {
    if (
      tag?.cardType === "FIRST_MANDATORY" ||
      tag?.cardType === "NEXT_MANDATORY"
    ) {
      return theme.colors.yellow["200"];
    } else {
      return theme.colors.green["100"];
    }
  };

  return (
    <>
      <OnGoingClassCardContainer>
        {tag && (
          <ClassTagContainer backgroundColor={getBackgroundColor()}>
            <StatusDot classType={tag?.cardType} />
            <div>{tag.title}</div>
            <StatusDot classType={tag?.cardType} />
          </ClassTagContainer>
        )}
        <OnGoingClassCard
          onClick={handleClick}
          data-testid="ongoing-class-card"
          isHeroSection={isHeroSection}
        >
          <OnGoingClassCardImageContainer>
            <OnGoingClassCardImageWrapper>
              <Image
                src={getMediaURL(baseMediaURL, classSession.teacher.avatarUrl)}
                alt={classSession.teacher.name}
                layout="fill"
                objectFit="fill"
              />
            </OnGoingClassCardImageWrapper>
          </OnGoingClassCardImageContainer>

          <OngoingClassCardDetails>
            <OngoingClassCardTopicsContainer>
              <li>
                <SubjectTag
                  backgroundColor={subjectTagData?.backgroundColor}
                  color={subjectTagData?.color}
                  text={subjectTagData?.text}
                  iconSrc={subjectTagData?.iconSrc}
                />
              </li>
              <ClassTags tags={classSession.tags} />
            </OngoingClassCardTopicsContainer>
            <OngoingClassCardDetailsHeading>
              {classSession.topic}
            </OngoingClassCardDetailsHeading>
            <OngoingClassCardDetailsContent>
              <OngoingClassCardDetailsTeacherContainer>
                <OngoingClassCardDetailsTeacher>
                  {classSession.teacher.name} <GuruJuaraBadge />
                </OngoingClassCardDetailsTeacher>
                <OngoingClassCardInfoItemContainer>
                  <OngoingClassCardInfoItem>
                    {getClassTimeStamp(classSession.startDate)}
                  </OngoingClassCardInfoItem>
                  <OngoingClassCardInfoItem>
                    {format(
                      getTimeStampFromDateString(classSession.startDate),
                      "k:mm"
                    )}
                  </OngoingClassCardInfoItem>
                  <OngoingClassCardInfoItem>
                    {classSession.duration} menit
                  </OngoingClassCardInfoItem>
                </OngoingClassCardInfoItemContainer>
              </OngoingClassCardDetailsTeacherContainer>
              <JoinClass
                classSession={classSession}
                subscriptionStatus={subscriptionStatus}
                classType={ClassSessionType.ONGOING}
                handleJoinClass={handleJoinClass}
                primaryUser={primaryUser}
                handleJoinQnA={handleJoinQnA}
                disabled={isJoinButtonDisabled}
                onShowPausedSubscriptionModal={
                  setIsPausedSubscriptionModalOpen.on
                }
              />
            </OngoingClassCardDetailsContent>
            <OngoingClassCardProgressContainer>
              {minutesElapsed > 0 &&
                minutesElapsed <= progressMessageThreshold && (
                  <OngoingClassCardProgressMessage>
                    Baru mulai {minutesElapsed} menit, nih. Masuk, yuk!
                  </OngoingClassCardProgressMessage>
                )}
              <OngoingClassCardProgressBarContainer>
                <OngoingClassCardProgressBarActive
                  value={classProgressPercentage}
                />
              </OngoingClassCardProgressBarContainer>
            </OngoingClassCardProgressContainer>
          </OngoingClassCardDetails>
        </OnGoingClassCard>
      </OnGoingClassCardContainer>

      {isPausedSubscriptionModalOpen && (
        <PauseSubscriptionModal
          isOpen={isPausedSubscriptionModalOpen}
          onClose={() => setIsPausedSubscriptionModalOpen.off()}
        />
      )}
    </>
  );
};

export { OngoingClassCard };
